import type { MetadataRoute } from "next";
import { ROAVE_API_BASE_URL, ROAVE_SITE_BASE_URL } from "@/config/endpoints";

export const SITE_ORIGIN = ROAVE_SITE_BASE_URL;

export const API_ORIGIN = ROAVE_API_BASE_URL.replace(/\/api\/?$/, "").replace(/\/+$/, "");

export const API_BASE_URL = ROAVE_API_BASE_URL;

export const SITE_NAME = process.env.NEXT_PUBLIC_SITE_NAME || "Roave Travel";

export const agentDiscoveryLinks = [
  '</.well-known/api-catalog>; rel="api-catalog"',
  '</openapi.json>; rel="service-desc"; type="application/vnd.oai.openapi+json"',
  '</docs/api>; rel="service-doc"',
  '</.well-known/agent-skills/index.json>; rel="describedby"; type="application/json"',
  '</.well-known/mcp/server-card.json>; rel="service-desc"; type="application/json"',
  '</.well-known/oauth-protected-resource>; rel="oauth-protected-resource"; type="application/json"',
];

export const contentSignals =
  "ai-train=no, search=yes, ai-input=yes";

export function jsonResponse(data: unknown, init?: ResponseInit) {
  return Response.json(data, {
    ...init,
    headers: {
      "Cache-Control": "public, max-age=3600, s-maxage=3600",
      ...(init?.headers || {}),
    },
  });
}

export function linksetResponse(data: unknown) {
  return new Response(JSON.stringify(data, null, 2), {
    headers: {
      "Content-Type": "application/linkset+json; charset=utf-8",
      "Cache-Control": "public, max-age=3600, s-maxage=3600",
    },
  });
}

export function absoluteUrl(path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

export const apiScopes = [
  "openid",
  "profile",
  "email",
  "hotels:read",
  "bookings:write",
  "payments:write",
];

export const roaveSkillMarkdown = `# Roave Travel Agent Skill

Use this skill when an AI agent needs to discover Roave Travel hotel search,
travel planning, booking, account, payment, content, and support resources.

## Discovery

- API catalog: ${absoluteUrl("/.well-known/api-catalog")}
- OpenAPI: ${absoluteUrl("/openapi.json")}
- OAuth protected resource metadata: ${absoluteUrl("/.well-known/oauth-protected-resource")}
- MCP server card: ${absoluteUrl("/.well-known/mcp/server-card.json")}
- x402 paid agent endpoint: ${absoluteUrl("/api/agent/payments/x402")}

## Useful Actions

- Search hotels by city, geolocation, dates, rooms, and nationality.
- Fetch static hotel details for a known hotel code.
- Browse Roave Travel blog guides and travel inspiration.
- Start booking or payment flows through the documented APIs.
- Detect x402 payment requirements by requesting the paid agent endpoint and
  reading the HTTP 402 \`PAYMENT-REQUIRED\` header.

## Content Preferences

Roave Travel allows search indexing and agent input use, but does not allow AI
training use by default. See robots.txt for Content-Signal directives.
`;

export async function sha256Digest(value: string) {
  const bytes = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(hash))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function staticSitemapEntry(path: string, priority = 0.7): MetadataRoute.Sitemap[number] {
  return {
    url: absoluteUrl(path),
    lastModified: new Date(),
    changeFrequency: "monthly",
    priority,
  };
}
